import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight, BookOpen, Layers, ClipboardCheck, Award, Mic, FolderOpen, Compass, BarChart3,
    FileText, Megaphone, Sparkles, Upload, Lightbulb, Type, Camera,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProgressBar from "@/components/ProgressBar";
import EmptyState from "@/components/EmptyState";

// Quick-access tiles. Filtered by the user's effective nav_keys (the union of
// their groups' allowed nav items — or the previewed group's keys if an admin
// is in Preview-As).
const QUICK_TILES = [
    { to: "/start", label: "Start Here", icon: Compass, key: "start-here" },
    { to: "/modules", label: "Modules", icon: BookOpen, key: "modules" },
    { to: "/flashcards", label: "Flash Cards", icon: Layers, key: "flashcards" },
    { to: "/quizzes", label: "Quizzes", icon: ClipboardCheck, key: "quizzes" },
    { to: "/certifications", label: "Certification", icon: Award, key: "certifications" },
    { to: "/resources", label: "Resources", icon: FolderOpen, key: "resources" },
    { to: "/roleplays", label: "Roleplays", icon: Mic, key: "roleplays" },
    { to: "/progress", label: "Progress", icon: BarChart3, key: "progress" },
];

// Group-specific hero cards. Each group gets up to three purpose-driven
// shortcuts that match how members of that group actually work.
const GROUP_CARDS = {
    "appointment-setters": {
        purpose: "Sharpen the language, build confidence, and earn certification.",
        cards: [
            { title: "Roleplay of the Day", body: "Run a fresh practice scenario.", icon: Mic, to: "/roleplays" },
            { title: "Scripts & Objection Handling", body: "Quick-reference the highest-leverage moves.", icon: FileText, to: "/resources" },
            { title: "Certification Progress", body: "See exactly what's left before you certify.", icon: Award, to: "/certifications" },
        ],
    },
    "marketing-partners": {
        purpose: "Stay aligned with the HAEVN brand so every campaign creates the right signal.",
        cards: [
            { title: "Latest Brand Assets", body: "Logos, photography, and the latest collateral.", icon: Sparkles, to: "/resources" },
            { title: "Campaign Guidance", body: "How HAEVN frames itself across paid and owned.", icon: Megaphone, to: "/modules" },
            { title: "Approved Language", body: "What we say, how we say it, what we don't.", icon: Type, to: "/resources" },
        ],
    },
    "ugc-creators": {
        purpose: "Understand the brand before you film, then create content that feels real.",
        cards: [
            { title: "Content Do's & Don'ts", body: "On-brand cues. Off-brand traps.", icon: Lightbulb, to: "/modules" },
            { title: "Upload Instructions", body: "How to submit content for review.", icon: Upload, to: "/resources" },
            { title: "Example Prompts & Hooks", body: "Scripts and openings that work.", icon: Camera, to: "/resources" },
        ],
    },
    "haevn-101": {
        purpose: "Get oriented to HAEVN: what we are, how it works, and how we talk about it.",
        cards: [
            { title: "Start Here", body: "A short orientation built for new arrivals.", icon: Compass, to: "/start" },
            { title: "What is HAEVN?", body: "The brand foundations in one module.", icon: BookOpen, to: "/modules" },
            { title: "Next Steps", body: "Where to go once you've completed HAEVN 101.", icon: ArrowUpRight, to: "/modules" },
        ],
    },
};

export default function Dashboard() {
    const { user, navVersion } = useAuth();
    const [summary, setSummary] = useState(null);
    const [modules, setModules] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [navFlags, setNavFlags] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/progress/summary").then((r) => r.data).catch(() => null),
            api.get("/modules").then((r) => r.data).catch(() => []),
            api.get("/quiz-attempts/me").then((r) => r.data).catch(() => []),
            api.get("/me/nav").then((r) => r.data).catch(() => null),
        ]).then(([s, m, a, nav]) => {
            setSummary(s); setModules(m); setAttempts(a); setNavFlags(nav);
        });
    }, [user?.id, user?.preview_groups?.length, navVersion]);

    // Effective group context — preview wins for admins.
    const effectiveGroups = useMemo(() => {
        const preview = user?.preview_groups || [];
        if (preview.length > 0) return preview;
        return user?.groups || [];
    }, [user?.groups, user?.preview_groups]);

    const groupLabel = effectiveGroups.length === 0
        ? null
        : effectiveGroups.map((g) => g.title).join(" + ");

    // Pull the group-specific card config for a SINGLE active group; multi-group
    // (or staff with none) gets the generic dashboard.
    const groupConfig = effectiveGroups.length === 1
        ? GROUP_CARDS[effectiveGroups[0].slug] || null
        : null;

    // Group-supplied purpose line (admin can author this per-group from
    // Admin → Groups → Purpose/intent). Falls back to the hardcoded card-config
    // purpose for the 4 base groups, then to a generic line.
    const groupPurpose = effectiveGroups.length === 1
        ? (effectiveGroups[0].purpose || groupConfig?.purpose || "")
        : "";

    const isStaff = user?.role === "admin" || user?.role === "trainer";
    const isPreviewing = (user?.preview_groups || []).length > 0;
    const navKeySet = useMemo(() => {
        if (isStaff && !isPreviewing) return null; // staff sees everything
        if (!navFlags) return null;
        return new Set(navFlags.nav_keys || []);
    }, [navFlags, isStaff, isPreviewing]);

    // Subtitle: prefer the group's authored purpose line.
    const subtitle = groupPurpose
        || (groupLabel ? `Your ${groupLabel} program. Pick up where you left off.` :
            (isStaff ? "Full content view. Every module, card and call across HAEVN University." :
                "Pick up where you left off. Every module brings you closer to certification."));

    const inProgress = modules.find((m) => m.progress_pct > 0 && m.progress_pct < 100);
    const upcoming = modules.find((m) => (m.progress_pct ?? 0) === 0);
    const firstModule = modules[0] ?? null;
    const recent = modules
        .filter((m) => (m.completed_lessons ?? 0) > 0)
        .slice(0, 4);

    // Quick tiles, filtered by nav_keys + content presence.
    const tiles = QUICK_TILES.filter((t) => {
        if (!navKeySet) return true;
        if (!navKeySet.has(t.key)) return false;
        if (t.key === "flashcards") return navFlags?.flashcards !== false;
        if (t.key === "quizzes") return navFlags?.quizzes !== false;
        if (t.key === "resources") return navFlags?.resources !== false;
        if (t.key === "roleplays") return navFlags?.roleplays !== false;
        return true;
    });

    // Stat visibility — hide tiles whose denominators are zero (e.g. no quizzes
    // for this group), and hide certification entirely when the group's nav_keys
    // omit it. This avoids "0/0" placeholder noise on lean programs.
    const showCertification = !!summary && summary.total_modules > 0
        && (!navKeySet || navKeySet.has("certifications"));
    const showLessons = !!summary && summary.total_lessons > 0;
    const showQuizzes = !!summary && summary.total_quizzes > 0;
    const showTotalModules = !!summary && summary.total_modules > 0;

    // Quiz attempts row: only when the user actually has attempts AND there are
    // quizzes in scope.
    const showAttemptsSection = attempts.length > 0 && (summary?.total_quizzes ?? 0) > 0;

    const date = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

    return (
        <div className="space-y-12" data-testid="dashboard-page">
            {/* Hero */}
            <section>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3" data-testid="dashboard-group-label">
                    {groupLabel || (isStaff ? "Operations" : date)}
                </div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05] max-w-3xl">
                    Welcome back, <span className="italic">{user?.name?.split(" ")[0] ?? ""}</span>.
                </h1>
                <p className="mt-3 text-base text-ink-500 max-w-2xl" data-testid="dashboard-subtitle">
                    {subtitle}
                </p>
            </section>

            {/* Stats — only visible counters render */}
            {(showCertification || showLessons || showQuizzes || showTotalModules) && (
                <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-300 border border-ink-300">
                    {showCertification && (
                        <Stat label="Certification" value={`${summary?.certification_pct ?? 0}%`} testId="stat-cert" />
                    )}
                    {showLessons && (
                        <Stat label="Lessons completed" value={`${summary.completed_lessons}/${summary.total_lessons}`} testId="stat-lessons" />
                    )}
                    {showQuizzes && (
                        <Stat label="Quizzes passed" value={`${summary.passed_quizzes}/${summary.total_quizzes}`} testId="stat-quizzes" />
                    )}
                    {showTotalModules && (
                        <Stat label="Total modules" value={`${summary.total_modules}`} testId="stat-modules" />
                    )}
                </section>
            )}

            {/* Continue + Upcoming */}
            <section className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-ink-300 p-8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-2">Continue learning</div>
                    {inProgress ? (
                        <Link to={`/modules/${inProgress.id}`} className="group block" data-testid="continue-module-link">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-heading text-2xl text-ink-900 group-hover:text-haevn-700 transition-colors">
                                        {inProgress.title}
                                    </h3>
                                    <p className="text-sm text-ink-500 mt-1 line-clamp-2 max-w-md">{inProgress.description || "Module description"}</p>
                                </div>
                                <ArrowUpRight size={20} strokeWidth={1.5} className="text-ink-400 group-hover:text-haevn-700 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </div>
                            <div className="mt-6">
                                <ProgressBar value={inProgress.progress_pct} label={`Module ${inProgress.completed_lessons}/${inProgress.lesson_count} lessons`} />
                            </div>
                        </Link>
                    ) : firstModule ? (
                        <Link to={`/modules/${firstModule.id}`} className="group block" data-testid="continue-first-module-link">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-heading text-2xl text-ink-900 group-hover:text-haevn-700 transition-colors">
                                        Begin with {firstModule.title}
                                    </h3>
                                    {firstModule.description && (
                                        <p className="text-sm text-ink-500 mt-1 line-clamp-2 max-w-md">{firstModule.description}</p>
                                    )}
                                </div>
                                <ArrowUpRight size={20} strokeWidth={1.5} className="text-ink-400 group-hover:text-haevn-700 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </div>
                            <div className="mt-6 text-xs font-mono uppercase tracking-[0.15em] text-haevn-700 group-hover:text-haevn-900">
                                {firstModule.estimated_minutes ?? 30} min · Start the program →
                            </div>
                        </Link>
                    ) : (
                        <div className="text-sm text-ink-500" data-testid="dashboard-empty-state">
                            <div className="font-heading text-2xl text-ink-900 mb-2">
                                {groupLabel ? `Nothing in ${groupLabel} yet.` : "No modules published yet."}
                            </div>
                            <p>
                                {groupLabel
                                    ? "Your administrator will assign modules to this group when content is ready."
                                    : "Your administrator will publish training content shortly."}
                            </p>
                        </div>
                    )}
                </div>

                <div className="bg-white border border-ink-300 p-8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Up next</div>
                    {upcoming ? (
                        <Link to={`/modules/${upcoming.id}`} data-testid="upcoming-module-link" className="block group">
                            <h3 className="font-heading text-lg text-ink-900 group-hover:text-haevn-700 transition-colors">{upcoming.title}</h3>
                            <p className="text-xs text-ink-500 mt-1">{upcoming.estimated_minutes ?? 30} min</p>
                            <div className="mt-4 text-xs font-mono uppercase tracking-[0.15em] text-ink-400 group-hover:text-haevn-700">Begin →</div>
                        </Link>
                    ) : (
                        <div className="text-sm text-ink-500">All modules in motion.</div>
                    )}
                </div>
            </section>

            {/* Group-specific cards — only when a single recognized group is active */}
            {groupConfig && (
                <section data-testid="dashboard-group-cards">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-4">For your role</div>
                    <div className="grid md:grid-cols-3 gap-px bg-ink-300 border border-ink-300">
                        {groupConfig.cards.map((c) => (
                            <Link
                                key={c.title}
                                to={c.to}
                                className="bg-white p-6 hover:bg-ink-100/60 transition-colors group flex flex-col"
                                data-testid={`group-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                            >
                                <c.icon size={20} strokeWidth={1.5} className="text-haevn-700 mb-3" />
                                <div className="font-heading text-base text-ink-900 group-hover:text-haevn-700 transition-colors">{c.title}</div>
                                <div className="mt-1 text-xs text-ink-500 line-clamp-2">{c.body}</div>
                                <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 group-hover:text-haevn-700">Open →</div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Quick access */}
            {tiles.length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-4">Quick access</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-ink-300 border border-ink-300" data-testid="dashboard-quick-tiles">
                        {tiles.map((q) => (
                            <Link
                                key={q.key}
                                to={q.to}
                                data-testid={`quick-${q.key}`}
                                className="bg-white p-5 hover:bg-ink-100 transition-colors group"
                            >
                                <q.icon size={18} strokeWidth={1.5} className="text-ink-500 group-hover:text-haevn-700 transition-colors" />
                                <div className="mt-3 text-sm font-medium text-ink-900">{q.label}</div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Recent activity */}
            <section>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-4">Recently active</div>
                {recent.length === 0 ? (
                    <EmptyState
                        title="No activity yet"
                        description="Complete a lesson to see your recent progress appear here."
                        icon={BookOpen}
                        testId="recent-empty"
                    />
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {recent.map((m) => (
                            <Link key={m.id} to={`/modules/${m.id}`} className="group bg-white border border-ink-300 p-6 hover:border-haevn-700 transition-colors" data-testid={`recent-module-${m.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{m.progress_pct}% complete</div>
                                <div className="mt-2 font-heading text-base text-ink-900 group-hover:text-haevn-700 transition-colors line-clamp-2">{m.title}</div>
                                <div className="mt-4">
                                    <ProgressBar value={m.progress_pct} size="sm" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Recent quiz attempts — only when group has quizzes AND user has attempts */}
            {showAttemptsSection && (
                <section data-testid="dashboard-attempts-section">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-4">Recent quiz attempts</div>
                    <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                        {attempts.slice(0, 5).map((a) => (
                            <div key={a.id} className="flex items-center justify-between px-6 py-4" data-testid={`attempt-row-${a.id}`}>
                                <div>
                                    <div className="text-sm text-ink-900">{a.correct}/{a.total} correct</div>
                                    <div className="text-xs text-ink-500 mt-0.5">{new Date(a.submitted_at).toLocaleString()}</div>
                                </div>
                                <div className={`text-xs font-mono uppercase tracking-[0.15em] ${a.passed ? "text-haevn-700" : "text-clay-500"}`}>
                                    {a.score}% · {a.passed ? "Passed" : "Retry"}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div className="bg-white p-6" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{label}</div>
            <div className="mt-3 font-heading text-3xl text-ink-900">{value}</div>
        </div>
    );
}
